import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Filter, RotateCcw, CheckSquare, Square } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Label } from '../../../ui/label';
import { Input } from '../../../ui/input';
import { StoredApplication } from '../../../utils/applicationStorage';

export interface AdvancedFilters {
    qualifications: string[];
    yearOfPassingFrom: string;
    yearOfPassingTo: string;
    experienceLevel: string[];
    experienceYearsFrom: string;
    experienceYearsTo: string;
    noticePeriods: string[];
    skills: string[];
}

interface AdvancedFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    applications: StoredApplication[];
    currentFilters: AdvancedFilters;
    onApplyFilters: (filters: AdvancedFilters) => void;
}

interface SkillWithCount {
    skill: string;
    count: number;
}

export const AdvancedFilterModal: React.FC<AdvancedFilterModalProps> = ({
    isOpen,
    onClose,
    applications,
    currentFilters,
    onApplyFilters
}) => {
    const [filters, setFilters] = useState<AdvancedFilters>(currentFilters);

    useEffect(() => {
        setFilters(currentFilters);
    }, [currentFilters, isOpen]);

    // Get dynamic options based on current applications
    const getAvailableQualifications = (): string[] => {
        const qualifications = new Set<string>();
        applications.forEach(app => {
            if (app.qualification) {
                qualifications.add(app.qualification);
            }
        });
        return Array.from(qualifications).sort();
    };

    const getAvailableNoticePeriods = (): string[] => {
        const periods = new Set<string>();
        applications.forEach(app => {
            if (app.noticePeriod) {
                periods.add(app.noticePeriod);
            }
        });
        return Array.from(periods).sort();
    };

    const getExperienceLevelCounts = (): { fresher: number; experienced: number } => {
        let fresher = 0;
        let experienced = 0;
        
        applications.forEach(app => {
            const appExpLevel = (app.experienceLevel || '').trim().toLowerCase();
            const appTotalExp = (app.totalExperience || app.experience || '').toLowerCase();
            
            // Check explicit experienceLevel first
            if (appExpLevel) {
                if (appExpLevel === 'fresher') {
                    fresher++;
                } else if (appExpLevel === 'experienced') {
                    experienced++;
                }
            } else {
                // Infer from totalExperience
                const expMatch = appTotalExp.match(/(\d+)/);
                const yearsExp = expMatch ? parseInt(expMatch[1]) : 0;
                
                const isFresherCandidate = yearsExp === 0 || 
                    appTotalExp.includes('fresher') || 
                    appTotalExp.includes('0 year') ||
                    appTotalExp === '' ||
                    appTotalExp === '0';
                
                if (isFresherCandidate) {
                    fresher++;
                } else if (yearsExp > 0) {
                    experienced++;
                }
            }
        });
        
        return { fresher, experienced };
    };

    const getAvailableSkillsWithCount = (): SkillWithCount[] => {
        const skillsMap = new Map<string, number>();
        applications.forEach(app => {
            if (app.skills && Array.isArray(app.skills)) {
                app.skills.forEach(skill => {
                    skillsMap.set(skill, (skillsMap.get(skill) || 0) + 1);
                });
            }
        });
        return Array.from(skillsMap.entries())
            .map(([skill, count]) => ({ skill, count }))
            .sort((a, b) => b.count - a.count);
    };

    const getYearRange = (): { min: number; max: number } => {
        const years: number[] = [];
        applications.forEach(app => {
            if (app.yearOfPassing) {
                const year = parseInt(app.yearOfPassing);
                if (!isNaN(year)) {
                    years.push(year);
                }
            }
        });
        return {
            min: years.length > 0 ? Math.min(...years) : 2000,
            max: years.length > 0 ? Math.max(...years) : new Date().getFullYear()
        };
    };

    const getExperienceRange = (): { min: number; max: number } => {
        const experiences: number[] = [];
        applications.forEach(app => {
            if (app.totalExperience) {
                // Extract number from strings like "5 years", "2-3 years", etc.
                const match = app.totalExperience.match(/(\d+)/);
                if (match) {
                    experiences.push(parseInt(match[1]));
                }
            }
        });
        return {
            min: 0,
            max: experiences.length > 0 ? Math.max(...experiences) : 20
        };
    };

    const availableQualifications = getAvailableQualifications();
    const availableNoticePeriods = getAvailableNoticePeriods();
    const availableSkills = getAvailableSkillsWithCount();
    const yearRange = getYearRange();
    const experienceRange = getExperienceRange();
    const experienceLevelCounts = getExperienceLevelCounts();

    const handleQualificationToggle = (qual: string) => {
        setFilters(prev => ({
            ...prev,
            qualifications: prev.qualifications.includes(qual)
                ? prev.qualifications.filter(q => q !== qual)
                : [...prev.qualifications, qual]
        }));
    };

    const handleExperienceLevelToggle = (level: string) => {
        setFilters(prev => ({
            ...prev,
            experienceLevel: prev.experienceLevel.includes(level)
                ? prev.experienceLevel.filter(l => l !== level)
                : [...prev.experienceLevel, level]
        }));
    };

    const handleNoticePeriodToggle = (period: string) => {
        setFilters(prev => ({
            ...prev,
            noticePeriods: prev.noticePeriods.includes(period)
                ? prev.noticePeriods.filter(p => p !== period)
                : [...prev.noticePeriods, period]
        }));
    };

    const handleSkillToggle = (skill: string) => {
        setFilters(prev => ({
            ...prev,
            skills: prev.skills.includes(skill)
                ? prev.skills.filter(s => s !== skill)
                : [...prev.skills, skill]
        }));
    };

    const handleReset = () => {
        const emptyFilters: AdvancedFilters = {
            qualifications: [],
            yearOfPassingFrom: '',
            yearOfPassingTo: '',
            experienceLevel: [],
            experienceYearsFrom: '',
            experienceYearsTo: '',
            noticePeriods: [],
            skills: []
        };
        setFilters(emptyFilters);
    };

    const handleApply = () => {
        onApplyFilters(filters);
        onClose();
    };

    const getActiveFilterCount = (): number => {
        let count = 0;
        if (filters.qualifications.length > 0) count++;
        if (filters.yearOfPassingFrom || filters.yearOfPassingTo) count++;
        if (filters.experienceLevel.length > 0) count++;
        if (filters.experienceYearsFrom || filters.experienceYearsTo) count++;
        if (filters.noticePeriods.length > 0) count++;
        if (filters.skills.length > 0) count++;
        return count;
    };

    if (!isOpen) return null;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-9999 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ duration: 0.2 }}
                        className="relative w-full max-w-4xl max-h-[90vh] glass-strong rounded-2xl border border-primary/30 shadow-2xl flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-border/30">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                                    <Filter className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <h2 className="text-foreground">Advanced Filters</h2>
                                    <p className="text-sm text-muted-foreground">
                                        Filter {applications.length} application{applications.length !== 1 ? 's' : ''}
                                        {getActiveFilterCount() > 0 && ` • ${getActiveFilterCount()} filter${getActiveFilterCount() !== 1 ? 's' : ''} active`}
                                    </p>
                                </div>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={onClose}
                                className="text-muted-foreground hover:text-foreground hover:bg-primary/10 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </Button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 px-6 py-6 overflow-y-auto modal-scrollable">
                            <div className="space-y-6">
                                {/* Highest Qualification */}
                                {availableQualifications.length > 0 && (
                                    <div className="space-y-3">
                                        <Label className="text-foreground">Highest Qualification</Label>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                            {availableQualifications.map(qual => (
                                                <button
                                                    key={qual}
                                                    type="button"
                                                    onClick={() => handleQualificationToggle(qual)}
                                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                                                        filters.qualifications.includes(qual)
                                                            ? 'bg-primary/20 border-primary/50 text-primary'
                                                            : 'glass border-border/30 text-foreground hover:border-primary/30'
                                                    }`}
                                                >
                                                    {filters.qualifications.includes(qual) ? (
                                                        <CheckSquare className="w-4 h-4" />
                                                    ) : (
                                                        <Square className="w-4 h-4" />
                                                    )}
                                                    <span className="truncate">{qual}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Year of Passing */}
                                <div className="space-y-3">
                                    <Label className="text-foreground">Year of Passing</Label>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-sm text-muted-foreground">From Year</Label>
                                            <Input
                                                type="number"
                                                placeholder={`${yearRange.min}`}
                                                value={filters.yearOfPassingFrom}
                                                onChange={(e) => setFilters(prev => ({ ...prev, yearOfPassingFrom: e.target.value }))}
                                                min={yearRange.min}
                                                max={yearRange.max}
                                                className="glass border-border/30 focus:border-primary/50"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm text-muted-foreground">To Year</Label>
                                            <Input
                                                type="number"
                                                placeholder={`${yearRange.max}`}
                                                value={filters.yearOfPassingTo}
                                                onChange={(e) => setFilters(prev => ({ ...prev, yearOfPassingTo: e.target.value }))}
                                                min={yearRange.min}
                                                max={yearRange.max}
                                                className="glass border-border/30 focus:border-primary/50"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Experience Level */}
                                <div className="space-y-3">
                                    <Label className="text-foreground">
                                        Experience Level (Fresher: {experienceLevelCounts.fresher}, Experienced: {experienceLevelCounts.experienced})
                                    </Label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {[
                                            { label: 'Fresher', count: experienceLevelCounts.fresher },
                                            { label: 'Experienced', count: experienceLevelCounts.experienced }
                                        ].map(({ label, count }) => (
                                            <button
                                                key={label}
                                                type="button"
                                                onClick={() => handleExperienceLevelToggle(label)}
                                                disabled={count === 0}
                                                className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                                                    filters.experienceLevel.includes(label)
                                                        ? 'bg-primary/20 border-primary/50 text-primary'
                                                        : count === 0
                                                        ? 'glass border-border/30 text-muted-foreground opacity-50 cursor-not-allowed'
                                                        : 'glass border-border/30 text-foreground hover:border-primary/30'
                                                }`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    {filters.experienceLevel.includes(label) ? (
                                                        <CheckSquare className="w-4 h-4" />
                                                    ) : (
                                                        <Square className="w-4 h-4" />
                                                    )}
                                                    <span>{label}</span>
                                                </div>
                                                <span className="text-xs px-2 py-0.5 rounded-full bg-background/50">
                                                    {count}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Years of Experience (only show if Experienced is selected) */}
                                {filters.experienceLevel.includes('Experienced') && (
                                    <div className="space-y-3">
                                        <Label className="text-foreground">Years of Experience</Label>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label className="text-sm text-muted-foreground">Minimum</Label>
                                                <Input
                                                    type="number"
                                                    placeholder="0"
                                                    value={filters.experienceYearsFrom}
                                                    onChange={(e) => setFilters(prev => ({ ...prev, experienceYearsFrom: e.target.value }))}
                                                    min={0}
                                                    max={experienceRange.max}
                                                    className="glass border-border/30 focus:border-primary/50"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-sm text-muted-foreground">Maximum</Label>
                                                <Input
                                                    type="number"
                                                    placeholder={`${experienceRange.max}`}
                                                    value={filters.experienceYearsTo}
                                                    onChange={(e) => setFilters(prev => ({ ...prev, experienceYearsTo: e.target.value }))}
                                                    min={0}
                                                    max={experienceRange.max}
                                                    className="glass border-border/30 focus:border-primary/50"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Notice Period */}
                                {availableNoticePeriods.length > 0 && (
                                    <div className="space-y-3">
                                        <Label className="text-foreground">Notice Period</Label>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                            {availableNoticePeriods.map(period => (
                                                <button
                                                    key={period}
                                                    type="button"
                                                    onClick={() => handleNoticePeriodToggle(period)}
                                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                                                        filters.noticePeriods.includes(period)
                                                            ? 'bg-primary/20 border-primary/50 text-primary'
                                                            : 'glass border-border/30 text-foreground hover:border-primary/30'
                                                    }`}
                                                >
                                                    {filters.noticePeriods.includes(period) ? (
                                                        <CheckSquare className="w-4 h-4" />
                                                    ) : (
                                                        <Square className="w-4 h-4" />
                                                    )}
                                                    <span className="truncate">{period}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Skills */}
                                {availableSkills.length > 0 && (
                                    <div className="space-y-3">
                                        <Label className="text-foreground">
                                            Skills ({availableSkills.length} available)
                                        </Label>
                                        <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2">
                                            {availableSkills.map(({ skill, count }) => (
                                                <button
                                                    key={skill}
                                                    type="button"
                                                    onClick={() => handleSkillToggle(skill)}
                                                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                                                        filters.skills.includes(skill)
                                                            ? 'bg-primary/20 border-primary/50 text-primary'
                                                            : 'glass border-border/30 text-foreground hover:border-primary/30'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        {filters.skills.includes(skill) ? (
                                                            <CheckSquare className="w-4 h-4" />
                                                        ) : (
                                                            <Square className="w-4 h-4" />
                                                        )}
                                                        <span className="truncate">{skill}</span>
                                                    </div>
                                                    <span className="text-xs px-2 py-0.5 rounded-full bg-background/50">
                                                        {count}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* No filters available message */}
                                {availableQualifications.length === 0 && 
                                 availableNoticePeriods.length === 0 && 
                                 availableSkills.length === 0 && (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <Filter className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                        <p className="text-center">No filter options available for the current selection</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-6 py-4 border-t border-border/30 gap-3">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleReset}
                                className="border-border/30 text-muted-foreground hover:text-foreground"
                            >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Reset All
                            </Button>
                            <div className="flex gap-3">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={onClose}
                                    className="border-border/30"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleApply}
                                    className="bg-primary hover:bg-primary/90 text-black"
                                >
                                    Apply Filters
                                    {getActiveFilterCount() > 0 && ` (${getActiveFilterCount()})`}
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body
    );
};
